/**
 * [ELICO] Free Mini Deo gift auto-add — 2026-09-04.
 *
 * Campaign: one free Mini Deo (Tropical) PER STARTER KIT is auto-added to the
 * cart (2 kits = 2 deos — Tobias 2026-09-05), and removed again if the kit
 * leaves. The gift line's quantity follows the kit count in both directions,
 * but only while the customer has not touched it themselves (we remember the
 * quantity WE set; a manual change wins). A server-side Discount Function
 * (SK-DEO, app elico-functions) frees one deo unit per kit unit at checkout —
 * this script computes no prices and never touches one; it only decides which
 * lines exist in the cart.
 *
 * Config is read from the `[data-elico-gift-config]` JSON block rendered in
 * sections/header.liquid, gated behind settings.elico_gift_deo_enabled.
 *
 * Runs on DOMContentLoaded and on every `theme:cart:refresh` (the event
 * assets/elico-cart-drawer.js dispatches on `document` after any cart
 * mutation, and that this script also dispatches after its own). A run is
 * debounced ~300ms and guarded by an in-flight flag, so overlapping fetches
 * are impossible. Loop-safety beyond that comes from idempotency: every
 * check() only mutates the cart when it finds real work left to do (rules c
 * / d below); once it has acted, the condition that triggered the mutation
 * no longer holds, so the re-entrant check() triggered by its own
 * `theme:cart:refresh` always finds nothing to do and exits as a no-op.
 */
(function () {
  'use strict';

  var CONFIG = (function () {
    var node = document.querySelector('[data-elico-gift-config]');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      return null;
    }
  })();
  // Bail silently — no config block means the campaign setting is off, or the
  // markup is missing; either way there is nothing safe to do here.
  if (!CONFIG || !CONFIG.kitProductId || !CONFIG.deoProductId || !CONFIG.giftVariantId) return;

  var PROP = '_elico_gift_deo';
  var ADDED_KEY = 'elicoGiftDeoAdded';
  var DISMISSED_KEY = 'elicoGiftDeoDismissed';
  var DEBOUNCE_MS = 300;

  function root() {
    var r = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    if (r.slice(-1) !== '/') r += '/';
    return r;
  }

  // sessionStorage can throw in locked-down browser contexts (private mode,
  // some in-app browsers) — never let that break the rest of the script.
  function getStorage(key) {
    try {
      return window.sessionStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }
  function setStorage(key, value) {
    try {
      window.sessionStorage.setItem(key, value);
    } catch (e) {}
  }
  function clearStorage(key) {
    try {
      window.sessionStorage.removeItem(key);
    } catch (e) {}
  }

  var warned = false;
  function warnOnce(err) {
    if (warned) return;
    warned = true;
    console.warn('[elico-gift-deo]', err);
  }

  function props() {
    var p = {};
    p[PROP] = '1';
    return p;
  }

  /** Same event assets/elico-cart-drawer.js listens on to re-render the drawer. */
  var selfRefresh = false;
  function refreshDrawer() {
    // Our own refresh would otherwise bounce straight back into scheduleCheck() and burn a
    // redundant /cart.js round trip (the state is already what we just made it). Event
    // dispatch is SYNCHRONOUS, so every listener has run and consumed the flag by the next
    // line — clearing it here means a later, unrelated refresh is never swallowed.
    selfRefresh = true;
    document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
    selfRefresh = false;
  }

  function addGift(qty) {
    var payload = { items: [{ id: CONFIG.giftVariantId, quantity: qty, properties: props() }] };
    return fetch(root() + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(function (r) {
        if (!r.ok) return r.json().then(function (d) { throw d; });
        return r.json();
      })
      .then(function () {
        setStorage(ADDED_KEY, String(qty));
        refreshDrawer();
      })
      .catch(warnOnce);
  }

  /** Bare /cart/change.js quantity write — no storage bookkeeping, no drawer refresh. */
  function setGiftQtyRaw(line, qty) {
    return fetch(root() + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: line.key, quantity: qty })
    }).then(function (r) {
      if (!r.ok) return r.json().then(function (d) { throw d; });
      return r.json();
    });
  }

  /** Sets the gift line to a new quantity (kit count changed under our feet). */
  function setGiftQty(line, qty) {
    return fetch(root() + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: line.key, quantity: qty })
    })
      .then(function (r) {
        if (!r.ok) return r.json().then(function (d) { throw d; });
        return r.json();
      })
      .then(function () {
        setStorage(ADDED_KEY, String(qty));
        refreshDrawer();
      })
      .catch(warnOnce);
  }

  /**
   * Collapses however many gift lines exist down to a single line of `qty`, which is what a
   * quantity change needs when Shopify has split the gift into paid + free halves. Sets the
   * first line to the target and zeroes the rest, sequentially (line keys shift as the cart
   * mutates, so these cannot be parallel). Only ever touches lines tagged as our gift.
   */
  function reconcileGiftQty(giftLines, qty) {
    var chain = setGiftQtyRaw(giftLines[0], qty);
    giftLines.slice(1).forEach(function (line) {
      chain = chain.then(function () { return setGiftQtyRaw(line, 0); });
    });
    return chain
      .then(function () {
        setStorage(ADDED_KEY, String(qty));
        refreshDrawer();
      })
      .catch(warnOnce);
  }

  /** Removes each gift line via a chained /cart/change.js quantity:0 (never touches other lines). */
  function removeGiftLines(giftLines) {
    var chain = Promise.resolve();
    giftLines.forEach(function (line) {
      chain = chain.then(function () {
        return fetch(root() + 'cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: line.key, quantity: 0 })
        }).then(function (r) {
          if (!r.ok) return r.json().then(function (d) { throw d; });
          return r.json();
        });
      });
    });
    return chain
      .then(function () {
        clearStorage(ADDED_KEY);
        refreshDrawer();
      })
      .catch(warnOnce);
  }

  function check(cart) {
    var lines = (cart && cart.items) || [];

    // a. Kit quantity across ALL lines/variants of the kit product (one-time or subscription).
    var kitQty = 0;
    lines.forEach(function (line) {
      if (line.product_id === CONFIG.kitProductId) kitQty += line.quantity;
    });

    // b. Split Mini Deo lines into ours (tagged with PROP) vs the customer's own — the
    //    latter is never touched by anything below.
    var giftLines = [];
    var otherDeoLines = [];
    lines.forEach(function (line) {
      if (line.product_id !== CONFIG.deoProductId) return;
      if (line.properties && Object.prototype.hasOwnProperty.call(line.properties, PROP)) {
        giftLines.push(line);
      } else {
        otherDeoLines.push(line);
      }
    });

    // g. Adopt a gift line we never recorded — either the buy box fast-added it inside its
    //    own /cart/add.js (see window.ElicoGiftDeo.giftItemFor) or sessionStorage was cleared.
    //    Recording it lets rule f keep the quantity in step with the kit, and lets rule e
    //    notice a later manual delete. Mutually exclusive with rule e (that needs 0 gift lines).
    var giftQtyTotal = 0;
    giftLines.forEach(function (l) { giftQtyTotal += l.quantity; });

    if (kitQty > 0 && giftLines.length > 0 && !getStorage(ADDED_KEY) && getStorage(DISMISSED_KEY) !== '1') {
      setStorage(ADDED_KEY, String(giftQtyTotal));
    }

    // e. (checked BEFORE the add on purpose: when the customer deletes the gift,
    //    this rule must win over rule c or the gift is re-added before the dismissal
    //    is ever recorded.) We had added a gift, the kit is still here, but the gift line is gone — the
    //    customer deleted it themselves. Remember that for the rest of the session so we
    //    never re-add it behind their back.
    if (getStorage(ADDED_KEY) && kitQty > 0 && giftLines.length === 0) {
      setStorage(DISMISSED_KEY, '1');
      clearStorage(ADDED_KEY);
      return Promise.resolve();
    }

    // c. Add the gift: kit present, no deo line at all yet (gift or own), not dismissed
    //    this session. One deo PER KIT (2 kits = 2 free deos).
    if (kitQty > 0 && giftLines.length === 0 && otherDeoLines.length === 0 && getStorage(DISMISSED_KEY) !== '1') {
      return addGift(kitQty);
    }

    // d. Kit gone: remove every gift line we added (there should only ever be one, but
    //    handle more defensively).
    if (kitQty === 0 && giftLines.length > 0) {
      return removeGiftLines(giftLines);
    }

    // f. Kit count changed while our gift line is in the cart: follow it — but ONLY
    //    while the current gift quantity still equals the one WE set. A customer who
    //    changed the gift quantity themselves owns that line from then on (the SK-DEO
    //    function still frees at most one unit per kit, so extras are simply paid).
    //    Exactly one untouched gift line, or Shopify may have split a diverged line —
    //    both split halves carry the property, so >1 lines also means hands off.
    if (kitQty > 0 && giftLines.length > 0 && getStorage(DISMISSED_KEY) !== '1') {
      var setQty = parseInt(getStorage(ADDED_KEY), 10) || 0;
      // Compare against the TOTAL across gift lines, not a single line. When the kit count
      // drops, SK-DEO frees fewer deo units than the line holds, so Shopify SPLITS the line
      // into a paid half and a free half and BOTH halves inherit the gift property. The old
      // `giftLines.length === 1` guard treated that split as "the customer touched it" and
      // handed off — leaving the shopper charged ~89 kr for a gift they never asked for
      // (reproduced on live 2026-09-06: 2 kits -> 1 kit left "Mini Deo x1 @8900 + x1 @0").
      // The total is still exactly what we set, so we do still own it; a genuine manual
      // change makes the total differ and we correctly keep our hands off.
      if (setQty > 0 && giftQtyTotal === setQty && kitQty !== giftQtyTotal) {
        return reconcileGiftQty(giftLines, kitQty);
      }
    }

    return Promise.resolve();
  }


  var inFlight = false;
  var debounceTimer = null;
  var lastRun = 0;
  var lastCart = null;

  /**
   * Fast path for the PDP buy box: returns the gift line to append to its OWN
   * /cart/add.js items array, so the free deo arrives in the SAME request as the kit
   * instead of a second round trip after the drawer has already painted.
   * Returns null whenever we are not certain it is safe — the reconciler below then
   * handles it exactly as before, so the worst case is today's behaviour.
   */
  function giftItemFor(kitQty) {
    if (!kitQty || kitQty < 1) return null;
    if (getStorage(DISMISSED_KEY) === '1') return null;
    if (!lastCart || !lastCart.items) return null; // no snapshot yet -> let the reconciler decide
    for (var i = 0; i < lastCart.items.length; i++) {
      // Any Mini Deo already in the cart (ours or the customer's own) means the rules in
      // check() apply — do not shortcut them.
      if (lastCart.items[i].product_id === CONFIG.deoProductId) return null;
    }
    return { id: CONFIG.giftVariantId, quantity: kitQty, properties: props() };
  }
  window.ElicoGiftDeo = { giftItemFor: giftItemFor };

  function runCheck() {
    // An overlapping call is a no-op by design — the run already in flight will pick up
    // the latest cart state on its own next pass via the refresh it triggers, if any.
    if (inFlight) return;
    inFlight = true;
    fetch(root() + 'cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (c) { lastCart = c; return check(c); })
      .catch(warnOnce)
      .then(function () {
        inFlight = false;
      });
  }

  // Leading-edge debounce. The old trailing-only version sat out a full DEBOUNCE_MS before
  // it even looked at the cart, which the customer saw as the free deo popping into an
  // already-open drawer. Now the first event in a burst runs immediately and the rest of the
  // burst is still coalesced, so we keep the loop-safety without the visible dead time.
  function scheduleCheck() {
    if (selfRefresh) return; // our own mutation — state is already correct
    window.clearTimeout(debounceTimer);
    var since = Date.now() - lastRun;
    if (since >= DEBOUNCE_MS) {
      lastRun = Date.now();
      runCheck();
    } else {
      debounceTimer = window.setTimeout(function () {
        lastRun = Date.now();
        runCheck();
      }, DEBOUNCE_MS - since);
    }
  }

  document.addEventListener('DOMContentLoaded', scheduleCheck);
  // theme:cart:refresh only fires on the DRAWER's own mutations (tray swap, qty,
  // discount ops). A plain add-to-cart fires theme:cart:added (after
  // cart-element.build()) and theme:cart:loaded instead — listen to all three so the
  // gift lands on the very first add, not the customer's next drawer interaction.
  document.addEventListener('theme:cart:refresh', scheduleCheck);
  document.addEventListener('theme:cart:added', scheduleCheck);
  document.addEventListener('theme:cart:loaded', scheduleCheck);
})();
